import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../config.js';

import '../../styles/global.css';
import '../../styles/requests.css';
import '../../styles/status.css';
import '../../styles/pagination.css';

import PageHeader from '../../components/general/pageHeader.js';
import PageTable from '../../components/general/pageTable';
import useAuthenticate from '../../components/authentication/authenticate.js';
import Loading from '../../components/general/loading.js';
import Filter from '../../components/general/filters';
import CreateRequestForm from '../../components/forms/createRequestForm';
import RequestCards from '../../components/cards/requestsCard.js';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const RRequests = () => {
    const { isLoading } = useAuthenticate();
    const queryClient = useQueryClient();

    const [selectedProperty, setSelectedProperty] = useState(null);
    const [addOverlayOpen, setAddOverlayOpen] = useState(false);

    // Filters
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [filterPanelVisible, setFilterPanelVisible] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    // Filtered requests
    const [filteredRequests, setFilteredRequests] = useState([]);

    const { data: properties } = useQuery({
        queryKey: ['properties'],
        queryFn: fetchProperties,
    });

    const { data: requests } = useQuery({
        queryKey: ['requestsByProperty', selectedProperty?.value],
        queryFn: () => fetchRequestsByProperty(selectedProperty.value),
        enabled: !!selectedProperty?.value,
    });

    useEffect(() => {
        if (properties?.length > 0 && !selectedProperty) {
            setSelectedProperty(properties[0]);
        }
    }, [properties, selectedProperty]);

    useEffect(() => {
        if (!requests || !selectedProperty) return;

        let filtered = requests.results.filter(
            (r) =>
                (typeof r.unit?.property === 'string'
                    ? r.unit.property
                    : r.unit?.property?._id) === selectedProperty?.value &&
                r.type !== 'WorkPermit' &&
                r.type !== 'GatePass'
        );

        if (typeFilter) {
            filtered = filtered?.filter((r) =>
                r.type?.toLowerCase().includes(typeFilter.toLowerCase())
            );
        }

        if (statusFilter) {
            filtered = filtered?.filter((r) =>
                r.status?.toLowerCase().includes(statusFilter.toLowerCase())
            );
        }

        filtered = filtered?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setFilteredRequests(filtered);
        setCurrentPage(1);
    }, [typeFilter, statusFilter, requests, selectedProperty]);

    const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredRequests.slice(indexOfFirstRow, indexOfLastRow);

    const maxVisibleButtons = 10;
    const startPage = Math.floor((currentPage - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
    const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

    const handleRequestCreated = () => {
        queryClient.invalidateQueries(['requestsByProperty', selectedProperty?.value]);
        setAddOverlayOpen(false);
    };

    return (
        <div className="full-page w-full">
            <PageHeader pageName="Service Requests" />
            {isLoading ? (
                <Loading />
            ) : (
                <>
                    <div className="page-header flex flex-col md:flex-row">
                        <div className="w-full md:w-[300px]">
                            <Autocomplete
                                options={properties}
                                value={selectedProperty}
                                onChange={(e, newVal) =>
                                    newVal?.value && newVal?.label
                                        ? setSelectedProperty(newVal)
                                        : setSelectedProperty(null)
                                }
                                isOptionEqualToValue={(option, value) =>
                                    option?.value === value?.value
                                }
                                getOptionLabel={(option) =>
                                    typeof option === 'string'
                                        ? option
                                        : option?.label || option?.name || ''
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Property"
                                        variant="outlined"
                                        fullWidth
                                    />
                                )}
                            />
                        </div>

                        {/* Buttons */}
                        <div className="flex md:flex-row gap-[10px] w-full md:w-auto mt-2 md:mt-0">
                            <button
                                className={`filter-button ${
                                    filterPanelVisible ? 'active' : ''
                                    } w-full md:w-auto h-[50px] md:h-auto text-[14px] md:text-[12px] `}
                                onClick={() => setFilterPanelVisible((prev) => !prev)}
                            >
                                <i className="fas fa-filter ml-1 leading-none"></i>
                                <span>Filter</span>
                            </button>
                            <button
                                className="header-button w-full md:w-auto h-[50px] md:h-auto text-[14px] md:text-[12px]"
                                onClick={() => setAddOverlayOpen(true)}
                            >
                                <i className="fas fa-add ml-1 leading-none"></i>
                                <span>Request</span>
                            </button>
                        </div>
                    </div>

                    {filterPanelVisible && (
                        <div className="filter-container">
                            <Filter
                                placeholder="Type"
                                options={[
                                    ...new Set(
                                        requests
                                        ?.map((r) => r.type)
                                        .filter((t) => t !== 'WorkPermit' && t !== 'GatePass')
                                    ),
                                ]}
                                value={typeFilter}
                                onChange={setTypeFilter}
                            />
                            <Filter
                                placeholder="Status"
                                options={[...new Set(requests?.map((r) => r.status))]}
                                value={statusFilter}
                                onChange={setStatusFilter}
                            />
                        </div>
                    )}

                    <div className="table-wrapper">
                        {/* Desktop Table */}
                        <div className="hidden md:block">
                            <PageTable
                                rows={currentRows}
                                columns={[
                                    {
                                    label: 'Unit',
                                    accessor: (row) => row.unit?.unitNumber || 'N/A',
                                    width: '5%',
                                    },
                                    {
                                    label: 'Status',
                                    accessor: (row) => {
                                        const status = row.status?.toLowerCase();
                                        return (
                                            <span className={`request-status-badge ${status}`}>
                                                {row.status}
                                            </span>
                                        );
                                    },
                                    width: '10%',
                                    },
                                    { label: 'Type', accessor: 'type', width: '10%' },
                                    {
                                    label: 'Title',
                                    accessor: (row) => row.details?.title || 'N/A',
                                    width: '20%',
                                    },
                                    {
                                    label: 'Priority',
                                    accessor: (row) => row.details?.priority,
                                    width: '10%',
                                    },
                                    {
                                    label: 'Requested On',
                                    accessor: (row) =>
                                        new Date(row.createdAt).toLocaleDateString(),
                                    width: '10%',
                                    },
                                ]}
                                hasButtons={false}
                            />
                        </div>

                        {/* Mobile Cards */}
                        <div className="block md:hidden">
                            <RequestCards rows={currentRows} />
                        </div>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="pagination-controls">
                            <div className="pagination-buttons">
                                <button
                                    className="pagination-button"
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                </button>

                                {startPage > 1 && (
                                    <button className="pagination-page" onClick={() => setCurrentPage(startPage - 1)} >
                                        ...
                                    </button>
                                )}

                                {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                                    const pageNum = startPage + i;
                                    return (
                                        <button
                                            key={pageNum}
                                            className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                                            onClick={() => setCurrentPage(pageNum)}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}

                                {endPage < totalPages && (
                                    <button className="pagination-page" onClick={() => setCurrentPage(endPage + 1)}>
                                        ...
                                    </button>
                                )}

                                <button
                                    className="pagination-button"
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                >
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {addOverlayOpen && selectedProperty && (
                        <CreateRequestForm
                        onClose={() => setAddOverlayOpen(false)}
                        property={{ _id: selectedProperty.value }}
                        onRequestCreated={handleRequestCreated}
                        />
                    )}
                </>
            )}
        </div>
    );
};

const fetchRequestsByProperty = async (propertyId) => {
    if (!propertyId) return [];
    const response = await axios.get(`${config.API_URL}/v2/request/property/${propertyId}`, {
        params: { sortBy: 'createdAt', sortOrder: 'desc', limit: 10 },
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    console.log('Fetched requests for property:', response.data);
    return response.data;
};

const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
    return response.data.map(item => ({
        value: item._id,
        label: item.name,
  }));
};

export default RRequests;